/**
 * SessionTransitionState — the `/app` guard surface for a session that is
 * being loaded or re-loaded.
 *
 * Task 306A §4.4. WHY THIS EXISTS RATHER THAN `LoadingState`, stated
 * plainly because the two look interchangeable and are not:
 *
 * `LoadingState`'s DEFAULT `variant="page"` accepts a `title` and does not
 * render it. Only the `inline` variant does. That is not a bug being fixed
 * here — `ErrorState.test.ts` pins it deliberately, and changing the page
 * variant is a repo-wide decision over ~59 call sites that stays filed as a
 * follow-up. But it made the two `/app` guard states on the
 * pending_activation → activated path render a document with NO TEXT NODES
 * AT ALL: six `.sk` rectangles, whose gradient is cream-on-cream. Staging
 * QA measured `document.body.innerText.length === 0` for the whole ~1.2 s
 * of that transition. §4.2's remedy — holding on «تم التفعيل، جاري تحديث
 * مساحة العمل…» — was passing that sentence to a component that drops it.
 *
 * So the transition surface states what it is waiting for, in text, and the
 * skeleton is decoration beneath it rather than the entire surface. `title`
 * and `description` are REQUIRED and there is no default: a caller cannot
 * reach a text-free surface by omission, which is exactly how the previous
 * one was reached.
 */

import { Skeleton } from './Skeleton';

interface SessionTransitionStateProps {
  readonly title: string;
  readonly description: string;
  readonly testId: string;
}

export function SessionTransitionState({
  title,
  description,
  testId,
}: SessionTransitionStateProps) {
  // The geometry deliberately matches `AuthGate`'s other full-page states and
  // `shell.css`'s centred `.state`, so moving from this surface to the blocking
  // `stalled` error — or to the no-tenant / session-error state — changes the
  // words and not the layout.
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid={testId}
      style={{
        padding: '64px 24px',
        maxWidth: 560,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 8,
      }}
    >
      <strong className="h-section">{title}</strong>
      <p className="sub" style={{ maxWidth: 420 }}>
        {description}
      </p>
      {/* Decoration. The live region above is what the surface SAYS; these
          carry no information and are hidden from assistive technology so
          they cannot be announced as an empty update. */}
      <div
        aria-hidden="true"
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
          marginTop: 12,
        }}
      >
        <Skeleton height={14} width="70%" />
        <Skeleton height={14} width="45%" />
        <Skeleton height={92} />
      </div>
    </div>
  );
}
